import ProbaWorkflowsSidebar from '@/components/probaworkflows_base_components/ProbaWorklfowsSidebar/ProbaWorkflowsSidebar.vue';
import navbar from "@/components/probaworkflows_base_components/Navbar/NavBar.vue";
import { verify_super_user_status } from "@/utils/constants/constants";

export default {
  name: 'ProbaWorkflowsLayout',
  components: {
    ProbaWorkflowsSidebar,
    navbar
  },
  data: () => ({
    is_sidebar_hidden: false,
    show_backdrop: false,
    isLargeScreen: window.innerWidth >= 1200,
    is_mobile: window.innerWidth < 768,
    dwypd_account_menu_open: false,
    is_super_user: false,
  }),

  computed: {
    // Computed property that reacts to route changes
    should_hide_sidebar() {
      return this.$route.name === 'view_all_user_forms' || this.$route.name === 'entity_application_overview' || this.$route.name === 'apply_for_jobs' || this.$route.name === 'entity_overview' || this.$route.name === 'entity_enquiry_management';
    },
    is_dwypd_recruitment_domain() {
      const hostname = window.location.hostname || "";
      return hostname.includes("dwypd") || hostname.includes("recruitment");
    },
    should_show_layout_topbar() {
      return !this.is_dwypd_recruitment_domain;
    },
    should_hide_layout_topbar_spacing() {
      return ['/company_management', '/form_management', '/form_management/', '/manage_questions', '/manage_questions/', '/view_template', '/view_template/', '/signing_workflow', '/signing_workflow/', '/status_management', '/status_management/'].includes(this.$route.path);
    },
    should_show_footer() {
      return this.$route.name !== 'manage_entity_type' && !this.is_dwypd_recruitment_domain;
    },
    dwypd_account_name() {
      const account_name = sessionStorage.getItem("lead_name") || sessionStorage.getItem("full_name") || "Account";
      return account_name;
    },
    dwypd_account_role() {
      const account_role = sessionStorage.getItem("user_role") || "User";
      return account_role;
    },
    dwypd_account_initial() {
      const account_name = this.dwypd_account_name.trim();
      const initial = account_name ? account_name.slice(0, 1).toUpperCase() : "N";
      return initial;
    },
    is_dwypd_management_nav_context() {
      const management_paths = [
        "/company_management",
        "/configure_company_management",
        "/user_management",
        "/action_management",
        "/form_management",
        "/manage_questions",
        "/view_template",
        "/signing_workflow",
        "/status_management",
      ];
      return management_paths.includes(this.$route.path);
    },
    dwypd_mobile_nav_items() {
      const nav_items = this.is_dwypd_management_nav_context
        ? this.get_dwypd_management_nav_items()
        : this.get_dwypd_recruitment_nav_items();
      return nav_items;
    }
  },

  mounted() {
    window.addEventListener("resize", this.checkScreenSize);
    this.sync_sidebar_visibility();
    this.check_dwypd_super_user_status();
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },

  watch: {
    // Watch computed property instead of $route directly
    should_hide_sidebar() {
      this.sync_sidebar_visibility();
    },
    $route() {
      this.close_dwypd_account_menu();
    }
  },

  methods: {
    /** Keep the DWYPD recruitment shell compact on tablet/mobile widths. */
    should_auto_hide_sidebar() {
      return this.should_hide_sidebar || (this.is_dwypd_recruitment_domain && window.innerWidth < 1200);
    },
    /** Sync sidebar visibility whenever the route or viewport changes. */
    sync_sidebar_visibility() {
      this.is_sidebar_hidden = this.should_auto_hide_sidebar();
    },
    /** Check whether the logged-in user can see super-user management nav items. */
    async check_dwypd_super_user_status() {
      this.is_super_user = await verify_super_user_status();
    },
    /** Return bottom-nav items for the selected-programme recruitment sidebar. */
    get_dwypd_recruitment_nav_items() {
      const nav_items = [
        {
          key: "overview",
          label: "Overview",
          path: "/overview",
          icon_class: "bi bi-house-fill",
          active_paths: ["/overview"],
          active_prefixes: ["/dashboard"],
        },
        {
          key: "manage_entity_type",
          label: "Job Profiles",
          path: "/manage_entity_type",
          icon_class: "bi bi-clipboard-fill",
          active_paths: ["/manage_entity_type"],
          active_prefixes: ["/view_job_profile"],
        },
        {
          key: "job_adverts",
          label: "Job Adverts",
          route: { path: "/view_entity_type", query: { all_adverts: "true" } },
          icon_class: "bi bi-pencil-square",
          active_paths: ["/view_entity_type"],
          active_prefixes: [],
        },
      ];
      return nav_items;
    },
    /** Return bottom-nav items for the programme-management sidebar. */
    get_dwypd_management_nav_items() {
      const nav_items = [
        {
          key: "company_management",
          label: "Programmes",
          path: "/company_management",
          icon_class: "bi bi-folder-fill",
          active_paths: ["/company_management", "/configure_company_management"],
          active_prefixes: [],
        },
        {
          key: "user_management",
          label: "Users",
          path: "/user_management",
          icon_class: "fas fa-users",
          active_paths: ["/user_management"],
          active_prefixes: [],
          super_user_only: true,
        },
        {
          key: "action_management",
          label: "Actions",
          path: "/action_management",
          icon_class: "fa-solid fa-screwdriver-wrench",
          active_paths: ["/action_management"],
          active_prefixes: [],
          super_user_only: true,
        },
        {
          key: "form_management",
          label: "Config",
          path: "/form_management",
          icon_class: "fa-solid fa-gear",
          active_paths: ["/form_management", "/manage_questions", "/view_template", "/signing_workflow", "/status_management"],
          active_prefixes: [],
          super_user_only: true,
        },
      ];
      const visible_nav_items = nav_items.filter(item => !item.super_user_only || this.is_super_user);
      return visible_nav_items;
    },
    toggle_sidebar() {
      this.is_sidebar_hidden = !this.is_sidebar_hidden;
      this.show_backdrop = !this.is_sidebar_hidden;
    },
    checkScreenSize() {
      this.isLargeScreen = window.innerWidth >= 1200;
      this.is_mobile = window.innerWidth < 768;
      this.sync_sidebar_visibility();
      if (this.isLargeScreen) {
        this.close_dwypd_account_menu();
      }
    },
    /** Toggle the compact DWYPD account menu on mobile/tablet. */
    toggle_dwypd_account_menu() {
      this.dwypd_account_menu_open = !this.dwypd_account_menu_open;
    },
    /** Close the compact DWYPD account menu. */
    close_dwypd_account_menu() {
      this.dwypd_account_menu_open = false;
    },
    /** Sign out from the mobile/tablet DWYPD account menu. */
    logout_dwypd_account() {
      localStorage.clear();
      sessionStorage.clear();
      this.close_dwypd_account_menu();
      this.$router.push("/");
    },
    /** Return a router destination for the existing DWYPD mobile nav item. */
    get_dwypd_mobile_nav_target(item) {
      const target = Reflect.get(item || {}, "route") || Reflect.get(item || {}, "path") || "/";
      return target;
    },
    /** Store the active sidebar key for existing DWYPD navigation behavior. */
    set_dwypd_mobile_nav_active(item) {
      const item_key = Reflect.get(item || {}, "key");
      if (!item_key) return;
      sessionStorage.setItem("sidebar_active_item_key", `hardcoded:${item_key}`);
    },
    /** Check whether the current route matches a DWYPD mobile nav item. */
    is_dwypd_mobile_nav_active(item) {
      const active_paths = Reflect.get(item || {}, "active_paths") || [];
      const active_prefixes = Reflect.get(item || {}, "active_prefixes") || [];
      const current_path = this.$route.path || "";
      return active_paths.includes(current_path) || active_prefixes.some(prefix => current_path.startsWith(prefix));
    }
  }
}
